import "./index.css";
import App from "./App";
import { Helmet } from "react-helmet";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import { reportWebVitals as reportCustomVitals } from "./utils/performance";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <div>
    <Helmet>
      {/* Primary Meta Tags */}
      <title>
        Md Ashraful Islam - Lead Frontend & Full-Stack Architect | React,
        Next.js, Fastify, AI Solutions
      </title>
      <meta charset="utf-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes"
      />
      <meta name="theme-color" content="#22d4fd" />
      <meta name="author" content="Md Ashraful Islam" />
      <meta name="generator" content="React 18.3.1" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Favicons and Icons */}
      <link rel="icon" type="image/png" sizes="32x32" href="/logo.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/logo.png" />
      <link rel="manifest" href="/manifest.json" />

      {/* SEO Meta Tags */}
      <meta
        name="title"
        content="Md Ashraful Islam - Lead Frontend & Full-Stack Architect | React, Next.js, Fastify, AI Solutions"
      />
      <meta
        name="description"
        content="🚀 Lead Frontend & Full-Stack Architect | Founder of Madmind Digitals. Architecting high-concurrency web applications, enterprise SaaS platforms, and AI-driven solutions."
      />
      <meta
        name="keywords"
        content="Md Ashraful Islam, Lead Frontend & Full-Stack Architect, Full Stack Engineer, Software Architect, React.js Developer, Next.js Expert, TypeScript, Fastify, PostgreSQL, Node.js, AI Integrations, Cloud SaaS, ByShelp, Wedly, Madmind Digitals, Frontend Architect"
      />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />

      {/* Geo Tags */}
      <meta name="geo.region" content="BD-55" />
      <meta name="geo.placename" content="Dhaka, Bangladesh" />
      <meta name="geo.position" content="23.8103;90.4125" />
      <meta name="ICBM" content="23.8103, 90.4125" />

      {/* Canonical URL */}
      <link
        rel="canonical"
        href={
          process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app/"
        }
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Md Ashraful Islam Portfolio" />
      <meta property="og:locale" content="en_US" />
      <meta
        property="og:url"
        content={
          process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app/"
        }
      />
      <meta
        property="og:title"
        content="Md Ashraful Islam - Lead Frontend & Full-Stack Engineer | React, Next.js, Fastify, AI Solutions"
      />
      <meta
        property="og:description"
        content="🚀 Lead Frontend & Full-Stack Engineer | Founder of Madmind Digitals. Architecting high-concurrency web applications, enterprise SaaS platforms, and AI-driven solutions."
      />
      <meta
        property="og:image"
        content={`${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/me2.png`}
      />
      <meta
        property="og:image:secure_url"
        content={`${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/me2.png`}
      />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Md Ashraful Islam - Lead Frontend & Full-Stack Engineer Portfolio"
      />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ashraful2880" />
      <meta name="twitter:creator" content="@ashraful2880" />
      <meta
        name="twitter:url"
        content={
          process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app/"
        }
      />
      <meta
        name="twitter:title"
        content="Md Ashraful Islam - Lead Frontend & Full-Stack Engineer | React, Next.js, Fastify, AI Solutions"
      />
      <meta
        name="twitter:description"
        content="🚀 Lead Frontend & Full-Stack Engineer | Founder of Madmind Digitals. Architecting high-concurrency web applications, enterprise SaaS platforms, and AI-driven solutions."
      />
      <meta
        name="twitter:image"
        content={`${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/banner.png`}
      />
      <meta
        name="twitter:image:alt"
        content="Md Ashraful Islam - Lead Frontend & Full-Stack Engineer Portfolio"
      />

      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="Ashraful Islam Portfolio" />
      <meta name="apple-mobile-web-app-title" content="Ashraful Islam" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="msapplication-config" content="/browserconfig.xml" />

      {/* Performance and Caching */}
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="format-detection" content="address=yes" />
      <meta name="format-detection" content="email=yes" />

      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link rel="preconnect" href="https://api.emailjs.com" />
      <link rel="dns-prefetch" href="https://cdn.emailjs.com" />

      {/* Rich Snippets Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#person`,
              name: "Md Ashraful Islam",
              alternateName: "Ashraful Islam",
              jobTitle: "Full Stack MERN Developer",
              description:
                "Full Stack MERN Developer specializing in React.js, Node.js, MongoDB, and Express.js. Expert in building scalable web applications and modern UI/UX solutions.",
              url:
                process.env.REACT_APP_SITE_URL ||
                "https://ashrafulislam.netlify.app",
              image: `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/me2.png`,
              sameAs: [
                "https://github.com/Ashraful2880",
                "https://www.linkedin.com/in/ashraful2880",
                "https://medium.com/@contact.ashraful1",
              ],
              address: {
                "@type": "PostalAddress",
                addressLocality: "Jhenaidah",
                addressCountry: "Bangladesh",
              },
              email: "contact.ashraful1@gmail.com",
              telephone: "+8801974238487",
              knowsAbout: [
                "React.js",
                "Node.js",
                "MongoDB",
                "Express.js",
                "JavaScript",
                "TypeScript",
                "Next.js",
                "Tailwind CSS",
                "REST API",
                "Full Stack Development",
                "MERN Stack",
                "Web Development",
                "Frontend Development",
                "Backend Development",
              ],
              hasOccupation: {
                "@type": "Occupation",
                name: "Full Stack Developer",
                occupationLocation: {
                  "@type": "Country",
                  name: "Bangladesh",
                },
                skills:
                  "React.js, Node.js, MongoDB, Express.js, JavaScript, TypeScript, Next.js, Tailwind CSS",
              },
            },
            {
              "@type": "WebSite",
              "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#website`,
              url:
                process.env.REACT_APP_SITE_URL ||
                "https://ashrafulislam.netlify.app",
              name: "Md Ashraful Islam Portfolio",
              description:
                "Portfolio website of Md Ashraful Islam, Full Stack MERN Developer from Bangladesh",
              publisher: {
                "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#person`,
              },
              inLanguage: "en-US",
            },
            {
              "@type": "WebPage",
              "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#webpage`,
              url:
                process.env.REACT_APP_SITE_URL ||
                "https://ashrafulislam.netlify.app",
              name: "Md Ashraful Islam - Full Stack MERN Developer | React, Node.js Expert",
              isPartOf: {
                "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#website`,
              },
              about: {
                "@id": `${process.env.REACT_APP_SITE_URL || "https://ashrafulislam.netlify.app"}/#person`,
              },
              description:
                "Portfolio and professional information of Md Ashraful Islam, Full Stack MERN Developer specializing in modern web technologies",
              inLanguage: "en-US",
            },
          ],
        })}
      </script>
    </Helmet>
    <App />
  </div>,
);

// Performance: Enhanced performance monitoring
if (process.env.REACT_APP_PERFORMANCE_MONITORING === "true") {
  reportCustomVitals((metric) => {});
}

reportWebVitals();
